<template>
  <div id="prospecting_analytics_grid">
    <InfoGrid :columns="3">
      <ContainerInfoItem
        v-for="(data, key) in analyticsData"
        :key="key"
        source="prospecting_analytics"
        :title="data.title"
        :description="range?.label || ''"
        :value="formatAnalyticsValue(data.value, data.unit)"
        :is-loading="data.isLoading"
      />
    </InfoGrid>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, watchEffect } from "vue";
import store from "@/src/store/index.js";
import {
  formatAnalyticsValue,
  toFetchCallCount,
  toFetchCompanyCount,
  toFetchContactCount,
  toFetchEmailSentCount,
  toFetchLeadCount,
} from "@/src/utils/analyticUtils.js";
import {
  analyticActions,
  analyticsUserTypes,
} from "@/src/constants/analyticConstants.js";
import InfoGrid from "@/src/components/info_components/InfoGrid.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const range = computed(() => store.getters["analytics/getRange"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const analyticsData = reactive({
  companies_added: { title: "Companies added", value: 0, isLoading: true },
  contacts_added: { title: "Contacts added", value: 0, isLoading: true },
  leads_added: { title: "Leads Added", value: 0, isLoading: true },
  companies_researched: {
    title: "Companies researched",
    value: 0,
    isLoading: true,
  },
  contacts_researched: {
    title: "Contacts researched",
    value: 0,
    isLoading: true,
  },
  leads_researched: { title: "Leads researched", value: 0, isLoading: true },
  emails_sent: { title: "Emails sent", value: 0, isLoading: true },
  calls_done: { title: "Calls done", value: 0, isLoading: true },
});

const getParams = () => ({
  user_type: analyticsUserTypes.USER,
  user_id: userIds.value,
  team_id: teamIds.value,
  start_time: range.value?.start,
  end_time: range.value?.end,
});

const updateAnalyticItem = (key, value) => {
  if (analyticsData[key]) {
    analyticsData[key].value = value ?? 0;
    analyticsData[key].isLoading = false;
  }
};

const fetchAnalytics = async () => {
  const params = getParams();
  Object.values(analyticsData).forEach((item) => (item.isLoading = true));

  toFetchContactCount({ ...params, action: analyticActions.CREATED })
    .then((result) => updateAnalyticItem("contacts_added", result))
    .catch(() => updateAnalyticItem("contacts_added", 0));

  toFetchContactCount({ ...params, action: analyticActions.MARKET_RESEARCH })
    .then((result) => updateAnalyticItem("contacts_researched", result))
    .catch(() => updateAnalyticItem("contacts_researched", 0));

  toFetchCompanyCount({ ...params, action: analyticActions.CREATED })
    .then((result) => updateAnalyticItem("companies_added", result))
    .catch(() => updateAnalyticItem("companies_added", 0));

  toFetchCompanyCount({ ...params, action: analyticActions.MARKET_RESEARCH })
    .then((result) => updateAnalyticItem("companies_researched", result))
    .catch(() => updateAnalyticItem("companies_researched", 0));

  toFetchLeadCount({ ...params, action: analyticActions.CREATED })
    .then((result) => updateAnalyticItem("leads_added", result))
    .catch(() => updateAnalyticItem("leads_added", 0));

  toFetchLeadCount({ ...params, action: analyticActions.MARKET_RESEARCH })
    .then((result) => updateAnalyticItem("leads_researched", result))
    .catch(() => updateAnalyticItem("leads_researched", 0));

  toFetchEmailSentCount({ ...params, action: "email_replied" })
    .then((result) => updateAnalyticItem("emails_sent", result))
    .catch(() => updateAnalyticItem("emails_sent", 0));

  toFetchCallCount({ ...params, action: "call_dialed" })
    .then((result) => updateAnalyticItem("calls_done", result))
    .catch(() => updateAnalyticItem("calls_done", 0));
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) fetchAnalytics();
});

useAnalyticsRefresh(fetchAnalytics, [range, userIds, teamIds]);
</script>
